import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
// SAB-1142: the same clear-day formatter the list badge uses, so the overlay and the
// badge cannot render different days for the same instant.
import { waiverClearLabel } from '@/lib/nflWaiverBadge';
import { NFLPlayerMeta } from './NFLPlayerMeta';
import { isStarterSlot, isIrSlot, slotLabel, leagueSlotOrder, isRosterFull } from '@/lib/nflRosterSlots';
import { isIREligible } from '@/lib/nflAvailability';
import { shortPlayerName, type GameContext } from '@/lib/nflGameLine';
import { useWeeklyProjections, formatWeeklyProj } from '@/hooks/useWeeklyProjections';
import { Input } from '@/components/ui/input';

/**
 * SAB-1016: the add/drop overlay.
 *
 * Replaces two dead ends. Desktop had a raw `fixed inset-0` card with a native <select> of
 * bare names, gated on a hardcoded `myRoster.length >= 21`. Mobile had nothing at all: Add
 * on a full roster fired, the server returned 409, and the user got a red toast telling them
 * to do something the UI gave them no way to do.
 *
 * ── THE DROP LIST IS COMPUTED BY THE SERVER ─────────────────────────────────
 *
 * Which rows are droppable comes from nfl_preview_add_drop, which runs the SAME bipartite
 * planner the writer runs. A client-side guess would be a second implementation of a rule
 * the server owns, and the two would drift -- so a row is enabled here only if the actual
 * planner says that drop yields a valid arrangement, and the disabled reason is the
 * planner's own, phrased for a human ("Leaves your K slot unfilled").
 *
 * Rows that cannot be dropped stay VISIBLE and greyed, following the SAB-989 PlayersTable
 * precedent. Hiding them would leave a manager staring at a short list wondering where their
 * kicker went.
 *
 * ── FAILURE IS THE SERVER'S TO DECIDE ───────────────────────────────────────
 *
 * The confirm button submits the pair and lets nfl_apply_add_drop rule on it. The preview
 * can go stale -- someone else can claim the player, or the roster can change in another
 * tab, between opening this and pressing confirm -- so the overlay never treats its own
 * preview as authority over the write. It surfaces whatever the server says.
 */

export interface AddDropTarget {
  id: string;
  full_name: string;
  position: string;
  nfl_team_abbr?: string | null;
  headshot_url?: string | null;
  /**
   * SAB-1030: the live status fields the IR decision reads. Optional because most callers
   * do not have them; absent reads as NOT eligible, which is the safe default — the server
   * re-checks against the database regardless, so a missing value costs a greyed
   * affordance, never a wrong placement.
   */
  status?: string | null;
  injury_status?: string | null;
}

interface RosterRow {
  player_id: string;
  position_slot: string;
  full_name: string;
  position: string;
  team_alias: string | null;
  bye_week: number | null;
  status: string | null;
  injury_status: string | null;
  game: GameContext | null;
  isBye: boolean;
}

interface PreviewRow { player_id: string; droppable: boolean; reason: string | null }

export default function NFLAddDropOverlay({
  open, onOpenChange, addPlayer, teamId, leagueId, seasonYear, currentWeek, rosterSlots,
  mode: modeProp = 'add', faabRemaining = null, onComplete,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  addPlayer: AddDropTarget | null;
  teamId: string | null;
  leagueId: string | null;
  /**
   * SAB-1017: 'add' is the instant free-agent path; 'bid' places a waiver claim.
   *
   * ONE overlay, two modes, rather than a second component. The drop picker is the
   * expensive part -- it calls the real planner to price every candidate -- and a bid needs
   * exactly the same list for exactly the same reason. A parallel component would be a
   * second place for that logic to drift.
   */
  mode?: 'add' | 'bid';
  /** Remaining FAAB, for the bid ceiling. Null while unknown — the server is the authority. */
  faabRemaining?: number | null;
  /** The league's `settings.roster_slots`, for construction-order display. */
  rosterSlots?: unknown;
  seasonYear: number | null;
  currentWeek: number | null;
  onComplete: (message: string) => void;
}) {
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [preview, setPreview] = useState<Map<string, PreviewRow>>(new Map());
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [step, setStep] = useState<'pick' | 'confirm'>('pick');
  /**
   * SAB-1142: the server can tell us mid-flow that this player is on WAIVERS.
   *
   * `mode` is chosen by the parent BEFORE the call, from the leaderboard's waiver_state.
   * That is right almost always, but it cannot be right always: kickoff can pass while the
   * overlay is open, and a stale list row is a normal race. When it happens the response
   * carries PLAYER_ON_WAIVERS, and the old code fell into `!res.ok || data.error` and
   * rendered a RED ERROR with no bid input -- an available action presented as a failure.
   *
   * So the response can UPGRADE the mode. Only ever add -> bid, never the reverse: a bid
   * downgraded to a free add would submit a different transaction than the one shown.
   */
  const [modeOverride, setModeOverride] = useState<'bid' | null>(null);
  const mode = modeOverride ?? modeProp;
  /** Informational, not an error. Set when the server routes us to waivers. */
  const [waiverNotice, setWaiverNotice] = useState<string | null>(null);
  const [waiverClearsAt, setWaiverClearsAt] = useState<string | null>(null);
  // Starts at '' rather than '0': a pre-filled 0 reads as a placed bid the manager did not
  // choose, and $0 is a legitimate bid so it cannot be distinguished from "not yet typed".
  const [bid, setBid] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) { setSelected(null); setStep('pick'); setError(null); setBid(''); setModeOverride(null); setWaiverNotice(null); setWaiverClearsAt(null); }
  }, [open]);

  const load = useCallback(async () => {
    if (!open || !addPlayer || !teamId || !leagueId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: rows } = await supabase
        .from('nfl_rosters' as never)
        .select('player_id, position_slot')
        .eq('team_id', teamId).eq('league_id', leagueId).eq('is_active', true);
      const rosterRows = (rows ?? []) as unknown as { player_id: string; position_slot: string }[];
      const ids = rosterRows.map(r => r.player_id);

      // Typed at the destructure, not the select: a multi-column select on these tables
      // tips the builder's conditional types into a SelectQueryError union and every
      // property read then scores on the typecheck ratchet. Same escape hatch the
      // nfl_games reads use across the NFL surfaces.
      const { data: playersRaw } = await supabase
        .from('nfl_players' as never).select('id, full_name, position, status, injury_status, nfl_franchise_id')
        .in('id', ids.length ? ids : ['00000000-0000-0000-0000-000000000000']);
      const players = (playersRaw ?? []) as unknown as {
        id: string; full_name: string; position: string;
        status: string | null; injury_status: string | null; nfl_franchise_id: string | null;
      }[];
      const pMap = new Map(players.map(p => [p.id, p]));

      const { data: franchisesRaw } = await supabase
        .from('nfl_franchises' as never).select('id, alias, bye_week_2026');
      const fMap = new Map(((franchisesRaw ?? []) as unknown as
        { id: string; alias: string | null; bye_week_2026: number | null }[]).map(f => [f.id, f]));

      // This week's games, for the same "Sun 4:25 PM vs DEN" line the matchup rows show.
      // All three of season_year, season_type and week are pinned — SAB-746/811/823 were
      // each caused by omitting one.
      const gMap = new Map<string, { scheduled: string; status: string; oppId: string | null; isHome: boolean }>();
      if (seasonYear && currentWeek) {
        const { data: games } = await (supabase.from('nfl_games' as never)
          .select('home_franchise_id, away_franchise_id, scheduled, status')
          .eq('season_year', seasonYear).eq('season_type', 'REG').eq('week', currentWeek) as never) as
          { data: { home_franchise_id: string; away_franchise_id: string; scheduled: string; status: string }[] | null };
        for (const g of games ?? []) {
          gMap.set(g.home_franchise_id, { scheduled: g.scheduled, status: g.status, oppId: g.away_franchise_id, isHome: true });
          gMap.set(g.away_franchise_id, { scheduled: g.scheduled, status: g.status, oppId: g.home_franchise_id, isHome: false });
        }
      }

      setRoster(rosterRows.map(r => {
        const p = pMap.get(r.player_id);
        const fid = p?.nfl_franchise_id ?? undefined;
        const g = fid ? gMap.get(fid) : undefined;
        return {
          player_id: r.player_id,
          position_slot: r.position_slot,
          full_name: p?.full_name ?? 'Unknown',
          position: p?.position ?? '??',
          team_alias: fid ? (fMap.get(fid)?.alias ?? null) : null,
          bye_week: fid ? (fMap.get(fid)?.bye_week_2026 ?? null) : null,
          status: p?.status ?? null,
          injury_status: p?.injury_status ?? null,
          isBye: !!fid && !g,
          game: g ? {
            scheduled: g.scheduled, status: g.status,
            opponentAlias: g.oppId ? (fMap.get(g.oppId)?.alias ?? null) : null,
            isHome: g.isHome,
          } : null,
        };
      }));

      const { data: pv, error: pvErr } = await supabase.rpc('nfl_preview_add_drop' as never, {
        p_team_id: teamId, p_league_id: leagueId, p_add_player: addPlayer.id,
      } as never);
      if (pvErr) throw new Error(pvErr.message);
      const cands = ((pv as unknown as { candidates?: PreviewRow[] })?.candidates ?? []);
      setPreview(new Map(cands.map(c => [c.player_id, c])));
    } catch (e) {
      // A failed preview must NOT silently enable everything: submitting a drop the planner
      // would refuse wastes the user's move and returns a 409 they cannot interpret.
      setError(e instanceof Error ? e.message : 'Could not load your roster.');
      setPreview(new Map());
    } finally {
      setLoading(false);
    }
  }, [open, addPlayer, teamId, leagueId, seasonYear, currentWeek]);

  useEffect(() => { void load(); }, [load]);

  const weeklyProj = useWeeklyProjections(
    useMemo(() => roster.map(r => r.player_id), [roster]),
    currentWeek,
  );

  // STARTERS then BENCH, per the reference design. IR is shown last and never droppable
  // here: an IR player does not occupy capacity (SAB-986), so dropping one frees nothing.
  const sections = useMemo(() => {
    // SAB-1016: display in ROSTER CONSTRUCTION ORDER -- QB, RB1, RB2, WR1, WR2, TE, FLEX,
    // K, P, DST, COACH, then BN1..BN6 -- from the league's own template, the same source
    // My Team and the draft rail read (`leagueSlotOrder`). The rows arrived in whatever
    // order Postgres returned them, which is why DST rendered first and QB seventh. A
    // manager scanning for someone to drop reads the roster in the shape they built it;
    // an arbitrary order makes them hunt.
    const order = leagueSlotOrder(rosterSlots);
    const rank = new Map(order.map((slot, i) => [slot, i]));
    // A slot absent from the template sorts last rather than to position 0 -- `undefined`
    // through a numeric comparator would silently float it to the top, which is the same
    // class of bug as the DB ordering being fixed here.
    const bySlot = (a: RosterRow, b: RosterRow) =>
      (rank.get(a.position_slot) ?? Number.MAX_SAFE_INTEGER) -
      (rank.get(b.position_slot) ?? Number.MAX_SAFE_INTEGER);

    const starters = roster.filter(r => isStarterSlot(r.position_slot)).sort(bySlot);
    const bench = roster.filter(r => !isStarterSlot(r.position_slot) && !isIrSlot(r.position_slot)).sort(bySlot);
    const ir = roster.filter(r => isIrSlot(r.position_slot)).sort(bySlot);
    return [
      { key: 'STARTERS', rows: starters },
      { key: 'BENCH', rows: bench },
      ...(ir.length ? [{ key: 'INJURED RESERVE', rows: ir }] : []),
    ];
  }, [roster, rosterSlots]);

  // ── Swipe-down to dismiss ────────────────────────────────────────────────
  //
  // Tracked in a ref, not state: a re-render per touchmove would fight the scroll and is
  // wasted work when nothing renders from the intermediate value. Only a deliberate
  // downward drag past the threshold closes -- a short pull settles back, so a mis-grab
  // while scrolling the drop list does not throw away the user's selection.
  const dragFrom = useRef<number | null>(null);
  const SWIPE_CLOSE_PX = 60;

  const onGrabStart = (e: React.TouchEvent) => { dragFrom.current = e.touches[0]?.clientY ?? null; };
  const onGrabMove = (e: React.TouchEvent) => {
    if (dragFrom.current === null) return;
    // Downward only: an upward drag on the handle is not a dismiss.
    if ((e.touches[0]?.clientY ?? 0) - dragFrom.current > SWIPE_CLOSE_PX) {
      dragFrom.current = null;
      onOpenChange(false);
    }
  };
  const onGrabEnd = () => { dragFrom.current = null; };

  /**
   * SAB-1017: in BID mode a drop is optional unless the roster is full.
   *
   * A claim is a request for a future award, so a manager with an open slot does not have
   * to give anyone up to place one. Fullness is derived from the same helper the desktop
   * router uses, so the two cannot disagree about what "full" means.
   */
  const rosterIsFull = useMemo(
    () => isRosterFull(rosterSlots, roster.map(r => r.position_slot)),
    [rosterSlots, roster],
  );
  /**
   * SAB-1030: can this add land straight on IR, with no drop?
   *
   * Needs BOTH an IR-eligible status and an open IR slot in the league's OWN template — a
   * league that declares no IR slot gets no affordance. The client only decides whether to
   * OFFER this; nfl-add-drop re-reads the player's status from the database and refuses a
   * healthy player with NOT_IR_ELIGIBLE, so a stale status here cannot park anyone.
   */
  const openIrSlot = useMemo(() => {
    const irSlots = leagueSlotOrder(rosterSlots).filter(isIrSlot);
    const used = new Set(roster.map(r => r.position_slot));
    return irSlots.find(sl => !used.has(sl)) ?? null;
  }, [rosterSlots, roster]);

  const irEligible = isIREligible(addPlayer?.injury_status, addPlayer?.status);
  const canStashOnIr = mode === 'add' && irEligible && !!openIrSlot;

  // An IR stash needs no drop even on a full roster — that is the whole point of the
  // ghost slot. Occupied IR, or a healthy player, falls back to the normal flow.
  const dropRequired = (mode === 'add' && !canStashOnIr) || (mode === 'bid' && rosterIsFull && !openIrSlot);

  const bidNum = Number(bid.trim());
  const bidValid =
    bid.trim() !== '' && Number.isInteger(bidNum) && bidNum >= 0 &&
    (faabRemaining === null || bidNum <= faabRemaining);
  // The ceiling is shown, not just enforced: a rejected bid with no stated limit makes a
  // manager guess. faabRemaining null means we could not read it — the server still refuses
  // an over-budget bid (nfl_debit_faab's WHERE clause), so this stays permissive rather
  // than blocking a legitimate claim on a failed read.
  const canProceed = mode === 'bid'
    ? bidValid && (!dropRequired || !!selected)
    : canStashOnIr || !!selected;

  const chosen = roster.find(r => r.player_id === selected) ?? null;

  const submit = async () => {
    if (!addPlayer || !teamId) return;
    if (mode === 'add' && !selected && !canStashOnIr) return;
    if (mode === 'bid' && !bidValid) return;
    if (mode === 'bid' && dropRequired && !selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${(supabase as unknown as { supabaseUrl: string }).supabaseUrl}/functions/v1/nfl-add-drop`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(
            mode === 'bid'
              // drop_player_id is omitted rather than sent as null when no drop was chosen:
              // the claim branch treats a present-but-null differently from absent.
              ? {
                  action: 'claim', team_id: teamId, player_id: addPlayer.id,
                  bid_amount: bidNum, ...(selected ? { drop_player_id: selected } : {}),
                }
              // An IR stash sends NO drop: the server's IR branch is reached only on a
              // no-drop add, and sending one would route it back through the planner.
              : { action: 'add', team_id: teamId, player_id: addPlayer.id,
                  ...(canStashOnIr && !selected ? {} : { drop_player_id: selected }) },
          ),
        },
      );
      const data = await res.json();

      // SAB-1142: PLAYER_ON_WAIVERS is not a failure -- it is "this is a claim, not an
      // add". Checked BEFORE the error arm because the body carries `error` too, and the
      // old order swallowed it into a red banner. Switching mode here is what puts the FAAB
      // input on screen; the manager stays in the flow and submits a bid.
      if (data?.code === 'PLAYER_ON_WAIVERS') {
        setModeOverride('bid');
        setWaiverNotice(typeof data.error === 'string' ? data.error : 'This player is on waivers — place a claim.');
        setWaiverClearsAt(typeof data.clears_at === 'string' ? data.clears_at : null);
        setError(null);
        setStep('pick');
        return;
      }

      if (!res.ok || data.error) { setError(data.error || (mode === 'bid' ? 'Claim failed' : 'Add failed')); setStep('pick'); return; }
      onComplete(data.message || (mode === 'bid'
        ? `Claim submitted for ${addPlayer.full_name}`
        : `Added ${addPlayer.full_name}`));
      onOpenChange(false);
    } catch {
      setError(mode === 'bid' ? 'Claim failed — please try again.' : 'Add failed — please try again.');
      setStep('pick');
    } finally {
      setSubmitting(false);
    }
  };

  if (!addPlayer) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="add-drop-overlay"
        hideClose
        className={cn(
          'p-0 flex flex-col gap-0 bg-background',
          // MOBILE: full-screen bottom sheet, unchanged.
          'h-[100dvh]',
          // DESKTOP (>=sm): a CENTERED MODAL at roughly the players-box width, not a
          // full-bleed bottom sheet. `side="bottom"` pins inset-x-0/bottom-0, so each of
          // those has to be undone explicitly -- left/right auto, bottom auto, then
          // centered with the standard 50%/-translate-1/2 pair. mx-auto alone will not
          // do it against `inset-x-0`.
          'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2',
          'sm:-translate-x-1/2 sm:-translate-y-1/2',
          'sm:w-[calc(100vw-4rem)] sm:max-w-2xl sm:h-[85vh] sm:rounded-xl sm:border',
          // The bottom-slide animation reads wrong on a centered box; fade instead.
          'sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0',
        )}
      >
        {/* Radix requires a title and description on the dialog or a screen reader
            announces an unlabelled dialog. The visible header below is styled and
            truncates, so these carry the same information unstyled and off-screen
            rather than being dropped. `sr-only` is not display:none — it stays in the
            accessibility tree. */}
        <SheetTitle className="sr-only">Add {addPlayer.full_name}</SheetTitle>
        <SheetDescription className="sr-only">
          Choose a player to drop to make room for {addPlayer.full_name}. Players who cannot
          be dropped are listed with the reason.
        </SheetDescription>

        {/* Grab handle. Doubles as the swipe-down affordance: Radix's Dialog has no
            drag-to-dismiss, so the gesture is handled here rather than pretending the
            primitive supports it. */}
        <div
          className="pt-safe-top flex-none flex justify-center pb-1 touch-none"
          onTouchStart={onGrabStart}
          onTouchMove={onGrabMove}
          onTouchEnd={onGrabEnd}
        >
          <div className="h-1 w-10 rounded-full bg-muted-foreground/30" aria-hidden />
        </div>

        {/* Header — who is coming in.
            The sheet is full-height on a phone, so in the WRAPPED APP its top edge is the
            physical top of the screen: without the inset above, this row sits under the
            status-bar clock. env() resolves to 0px in a browser, so the same markup is
            correct in both. */}
        <div className="flex-none border-b border-border px-4 pb-3 pt-1 flex items-center gap-3">
          {addPlayer.headshot_url && (
            <img src={addPlayer.headshot_url} alt="" className="h-10 w-10 rounded-full object-cover bg-muted" />
          )}
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-bold text-foreground truncate">Add {addPlayer.full_name}</p>
            <p className="text-[11px] text-muted-foreground">
              {addPlayer.position}{addPlayer.nfl_team_abbr ? ` · ${addPlayer.nfl_team_abbr}` : ''}
            </p>
          </div>
          {/* The ONLY close in this sheet: SheetContent's built-in is suppressed with
              hideClose, so the two stacked X's are now one. 44x44 is the iOS minimum tap
              target; the icon stays small while the hit area is not. -mr-2 keeps the
              enlarged box from pushing the title in. */}
          <button
            type="button"
            aria-label="Close"
            data-testid="add-drop-close"
            onClick={() => onOpenChange(false)}
            className="h-11 w-11 -mr-2 flex-none flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground active:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {canStashOnIr ? (
          // SAB-1030: the primary action, replacing the drop prompt. The candidate list
          // still renders so a manager CAN choose a drop instead — but nothing asks them to,
          // because nothing needs to.
          <div className="flex-none bg-emerald-400/10 border-b border-emerald-400/20 px-4 py-2">
            <p data-testid="ir-stash-notice" className="text-[11px] text-emerald-300">
              {addPlayer.full_name} is injured — add straight to {openIrSlot}, no drop needed.
            </p>
          </div>
        ) : mode === 'bid' ? (
          <div className="flex-none border-b border-border px-4 py-3 space-y-2">
            <label className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground shrink-0">Place bid</span>
              <span className="text-xs text-muted-foreground">$</span>
              <Input
                data-testid="bid-amount"
                type="number" min={0} inputMode="numeric" placeholder="0"
                value={bid}
                onChange={e => setBid(e.target.value)}
                className="h-8 w-24 text-xs"
              />
              {faabRemaining !== null && (
                <span className="text-[11px] text-muted-foreground">of ${faabRemaining} left</span>
              )}
            </label>
            {/* Says WHY the button is disabled. A greyed control with no reason is the
                dead-end this whole ticket exists to remove. */}
            {bid.trim() !== '' && !bidValid && (
              <p data-testid="bid-error" className="text-[11px] text-destructive">
                {faabRemaining !== null && Number.isInteger(bidNum) && bidNum > faabRemaining
                  ? `That is more than your remaining $${faabRemaining}.`
                  : 'Enter a whole dollar amount — $0 is allowed.'}
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              {dropRequired
                ? 'Your roster is full — choose a player to drop if this claim wins.'
                : 'Optional: choose a player to drop if this claim wins.'}
            </p>
          </div>
        ) : (
          <div className="flex-none bg-amber-400/10 border-b border-amber-400/20 px-4 py-2">
            <p className="text-[11px] text-amber-300">Your roster is full — select a player to drop.</p>
          </div>
        )}

        {/* SAB-1142: an AVAILABLE ACTION, not a failure. Deliberately not the destructive
            styling used below -- the manager is being routed to a claim, and red reads as
            "you cannot do this" when the correct reading is "do this instead". */}
        {waiverNotice && (
          <div data-testid="waiver-notice" className="flex-none bg-sky-400/10 border-b border-sky-400/20 px-4 py-2">
            <p className="text-[11px] text-sky-300">
              {waiverNotice}
              {(() => {
                // The clear DAY in the viewer's zone, from the server's instant. Same
                // formatter the list badge uses, so the two cannot say different days.
                const day = waiverClearLabel(waiverClearsAt);
                return day ? ` Clears ${day}.` : '';
              })()}
            </p>
          </div>
        )}
        {error && (
          <div data-testid="add-drop-error" className="flex-none bg-destructive/10 border-b border-destructive/20 px-4 py-2">
            <p className="text-[11px] text-destructive">{error}</p>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : sections.map(sec => (
            <div key={sec.key}>
              <p className="sticky top-0 z-10 bg-muted/60 backdrop-blur px-4 py-1.5 font-display text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                {sec.key}
              </p>
              {sec.rows.map(r => {
                const pv = preview.get(r.player_id);
                const isIr = isIrSlot(r.position_slot);
                // No preview row (load failed, or an IR player the server did not evaluate)
                // means NOT selectable. Defaulting to enabled would offer a move the writer
                // will refuse.
                const disabled = isIr || !pv?.droppable;
                const reason = isIr ? 'On IR — dropping frees no roster space.' : pv?.reason ?? null;
                const isSel = selected === r.player_id;
                return (
                  <label
                    key={r.player_id}
                    data-testid="drop-candidate"
                    data-droppable={disabled ? 'false' : 'true'}
                    className={cn(
                      'flex items-center gap-3 px-4 py-2 border-b border-border/50 cursor-pointer',
                      disabled && 'opacity-[0.55] cursor-not-allowed',
                      isSel && 'bg-primary/[0.08]',
                    )}
                    title={reason ?? undefined}
                  >
                    <input
                      type="radio"
                      name="drop-candidate"
                      className="h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
                      disabled={disabled}
                      checked={isSel}
                      onChange={() => setSelected(r.player_id)}
                      aria-label={reason ? `${r.full_name} — ${reason}` : `Drop ${r.full_name}`}
                    />
                    <span className="w-9 shrink-0 font-display text-[10px] font-bold text-muted-foreground">
                      {slotLabel(r.position_slot)}
                    </span>
                    <NFLPlayerMeta
                      className="flex-1"
                      fullName={r.full_name}
                      shortName={shortPlayerName(r.full_name)}
                      position={r.position}
                      teamAlias={r.team_alias}
                      byeWeek={r.bye_week}
                      injuryStatus={r.injury_status}
                      status={r.status}
                      isBye={r.isBye}
                      game={r.game}
                    />
                    {/* The projection is the whole point of showing these rows: it is the
                        add-versus-drop comparison the manager is actually making. */}
                    <span className="w-12 shrink-0 text-right tabular-nums text-[11px] text-muted-foreground">
                      {formatWeeklyProj(weeklyProj.get(r.player_id))}
                    </span>
                  </label>
                );
              })}
              {sec.rows.length === 0 && (
                <p className="px-4 py-3 text-[11px] text-muted-foreground">None.</p>
              )}
            </div>
          ))}
          {!loading && reasonsAllDisabled(sections, preview) && (
            <p data-testid="no-valid-drop" className="px-4 py-4 text-[11px] text-muted-foreground">
              No player on your roster can be dropped to make room for {addPlayer.full_name}.
            </p>
          )}
        </div>

        <div className="flex-none border-t border-border px-4 py-3">
          {step === 'pick' ? (
            <Button
              data-testid="add-drop-next"
              className="w-full"
              disabled={!canProceed || loading}
              onClick={() => setStep('confirm')}
            >
              {canStashOnIr && !selected ? 'Add to IR — no drop needed' : 'Next'}
            </Button>
          ) : (
            <div className="space-y-2">
              <p className="text-[11px] text-muted-foreground">
                {canStashOnIr && !chosen ? (
                  <>
                    Add <span className="text-foreground font-medium">{addPlayer.full_name}</span> to{' '}
                    <span className="text-foreground font-medium">{openIrSlot}</span>? Your active
                    roster is unchanged.
                  </>
                ) : mode === 'bid' ? (
                  <>
                    Bid <span className="text-foreground font-medium">${bidNum}</span> on{' '}
                    <span className="text-foreground font-medium">{addPlayer.full_name}</span>
                    {chosen ? <> and drop <span className="text-foreground font-medium">{chosen.full_name}</span></> : null}?
                    {/* SAB-1142c: THIS claim's own processing time, from the clears_at the
                        server returned with PLAYER_ON_WAIVERS — not a helper asserting one
                        schedule for every claim.

                        This is the bid-time surface, and it was the worst place to be wrong:
                        it told a manager bidding on a player whose game had already been
                        played that his claim resolved tonight, when it resolves on the
                        league's weekly waiver day — five days later.

                        Silent when the server did not give us an instant, rather than
                        falling back to a guess. */}
                    {(() => {
                      const when = waiverClearsAt ? waiverClearLabel(waiverClearsAt) : null;
                      return when
                        ? <>{' '}This claim processes {when}.</>
                        : null;
                    })()}
                  </>
                ) : (
                  <>
                    Add <span className="text-foreground font-medium">{addPlayer.full_name}</span> and drop{' '}
                    <span className="text-foreground font-medium">{chosen?.full_name}</span>?
                  </>
                )}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" disabled={submitting} onClick={() => setStep('pick')}>
                  Back
                </Button>
                <Button data-testid="add-drop-confirm" className="flex-1" disabled={submitting} onClick={submit}>
                  {submitting
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : mode === 'bid' ? 'Place bid' : (canStashOnIr && !chosen ? 'Add to IR' : 'Confirm')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** True when nothing on the roster can free a slot — worth saying out loud rather than
 *  leaving the manager to scan a fully greyed list looking for the one enabled row. */
function reasonsAllDisabled(
  sections: { rows: { player_id: string; position_slot: string }[] }[],
  preview: Map<string, PreviewRow>,
): boolean {
  const all = sections.flatMap(s => s.rows);
  if (all.length === 0) return false;
  return all.every(r => isIrSlot(r.position_slot) || !preview.get(r.player_id)?.droppable);
}
